import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import {
  ConversationFilterNames,
  conversationFilterData,
} from "@redotech/effect-api-shared/schema/conversation-filters";
import {
  ARRAY_OPERATORS,
  DATE_COMPARISON_OPERATORS,
  NULL_CHECK_OPERATORS,
  STATUS_OPERATORS,
  STRING_PATTERN_OPERATORS,
  type ArrayOperator,
  type DateComparisonOperator,
  type EqualityOperator,
  type NullCheckOperator,
  type StringPatternOperator,
} from "@redotech/effect-api-shared/schema/query-options";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import type { DateFilterData } from "@redotech/redo-model/views/advanced-filters/date-filter";
import {
  DateFilterOperator,
  getAfterDateForDateFilter,
  getBeforeDateForDateFilter,
  getWithinDatesForDateFilter,
  type KnownDateFilterTimeFrame,
} from "@redotech/redo-model/views/advanced-filters/date-filter";
import type { ExistsFilterData } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import type { Ref, SimpleComparison } from "@tanstack/react-db";
import {
  and,
  eq,
  gt,
  gte,
  inArray,
  isNull,
  lt,
  lte,
  not,
  or,
} from "@tanstack/react-db";

type TanstackExpression = ReturnType<typeof eq>;

/**
 * The ref type for a non-object field T (leaf node in the TanStack ref proxy tree).
 * Extracts the same type that `Ref<{ _: T }>['_']` produces at runtime.
 */
type FieldRef<T> = Ref<{ _: T }>["_"];

function isArrayStringFilter(
  filter: AdvancedFilterData,
): filter is ArrayStringFilterData {
  return filter.type === AdvancedFilterType.ARRAY_TO_STRING;
}

function isExistsFilter(
  filter: AdvancedFilterData,
): filter is ExistsFilterData {
  return filter.type === AdvancedFilterType.EXISTS;
}

function isDateFilter(filter: AdvancedFilterData): filter is DateFilterData {
  return filter.type === AdvancedFilterType.DATE;
}

/**
 * Combines an array of expressions with AND or OR, returning null for empty.
 */
function combineExpressions(
  expressions: TanstackExpression[],
  operator: FilterOperator,
): TanstackExpression | null {
  if (expressions.length === 0) {
    return null;
  }
  return expressions.reduce((acc, expr) =>
    operator === FilterOperator.AND ? and(acc, expr) : or(acc, expr),
  );
}

/**
 * Builds a TanStack expression for a scalar string field with ANY_OF / NONE_OF.
 */
function scalarFieldAnyOfNoneOf(
  fieldRef: FieldRef<string | null>,
  filter: ArrayStringFilterData,
): TanstackExpression | null {
  const values = filter.value ?? [];
  if (values.length === 0) {
    return null;
  }
  switch (filter.operator) {
    case ArrayStringFilterOperator.ANY_OF:
      return values.length === 1
        ? eq(fieldRef, values[0])
        : inArray(fieldRef, values);
    case ArrayStringFilterOperator.NONE_OF:
      return values.length === 1
        ? not(eq(fieldRef, values[0]))
        : not(inArray(fieldRef, values));
    default:
      return null;
  }
}

/**
 * Builds a TanStack expression that checks if any of the given values
 * exist in an array field on the conversation.
 */
function arrayFieldContainsAny<T extends string>(
  arrayRef: FieldRef<readonly T[]>,
  values: T[],
): TanstackExpression | null {
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return inArray(values[0], arrayRef);
  }
  const expressions = values.map((v) => inArray(v, arrayRef));
  return or(expressions[0], expressions[1], ...expressions.slice(2));
}

/**
 * Builds a TanStack expression that checks if all of the given values
 * exist in an array field on the conversation.
 */
function arrayFieldContainsAll<T extends string>(
  arrayRef: FieldRef<readonly T[]>,
  values: T[],
): TanstackExpression | null {
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return inArray(values[0], arrayRef);
  }
  const expressions = values.map((v) => inArray(v, arrayRef));
  return and(expressions[0], expressions[1], ...expressions.slice(2));
}

/**
 * Builds a TanStack expression for a date filter.
 */
function dateFilterToExpression(
  filter: DateFilterData,
  fieldRef: FieldRef<Date> | null,
): TanstackExpression | null {
  if (!fieldRef) {
    return null;
  }
  const value = filter.value as KnownDateFilterTimeFrame | null;
  if (!value) {
    return null;
  }

  try {
    switch (filter.operator) {
      case DateFilterOperator.WITHIN: {
        const dates = getWithinDatesForDateFilter(
          value,
          filter.customDate,
          undefined,
          filter.relativeTime,
        );
        if (!dates) {
          return null;
        }
        return and(gte(fieldRef, dates.start), lte(fieldRef, dates.end));
      }
      case DateFilterOperator.BEFORE: {
        const date = getBeforeDateForDateFilter(
          value,
          filter.customDate,
          undefined,
          filter.relativeTime,
        );
        if (!date) {
          return null;
        }
        return lt(fieldRef, date);
      }
      case DateFilterOperator.AFTER: {
        const date = getAfterDateForDateFilter(
          value,
          filter.customDate,
          undefined,
          filter.relativeTime,
        );
        if (!date) {
          return null;
        }
        return gt(fieldRef, date);
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/**
 * Converts a single AdvancedFilterData to a TanStack DB expression.
 *
 * Note: Only handles filters that can be applied client-side to TanStack DB.
 * Complex filters (date ranges, tags requiring joins, etc.) return null
 * and are handled server-side via OpenSearch.
 */
export function advancedFilterToTanstackExpression(
  filter: AdvancedFilterData,
  conversationRef: Ref<ConversationListItemResponse>,
): TanstackExpression | null {
  switch (filter.name) {
    case ConversationFilterNames.STATUS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      return scalarFieldAnyOfNoneOf(conversationRef.status, filter);
    }

    case ConversationFilterNames.READ_AT: {
      if (!isExistsFilter(filter)) {
        return null;
      }
      const fieldRef = conversationRef.readAt;

      switch (filter.operator) {
        case ExistsFilterOperator.EXISTS:
          return not(isNull(fieldRef));
        case ExistsFilterOperator.NOT_EXISTS:
          return isNull(fieldRef);
        default:
          return null;
      }
    }

    case ConversationFilterNames.WORDS: {
      return null;
    }

    case ConversationFilterNames.ASSIGNEES: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      return scalarFieldAnyOfNoneOf(conversationRef.mongoAssigneeId, filter);
    }

    case ConversationFilterNames.CHANNELS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      const channelsRef = conversationRef.channels;
      const channelValues = filter.value ?? [];

      if (channelValues.length === 0) {
        return null;
      }

      switch (filter.operator) {
        case ArrayStringFilterOperator.ANY_OF:
          return arrayFieldContainsAny(channelsRef, channelValues);
        case ArrayStringFilterOperator.NONE_OF: {
          const anyExpr = arrayFieldContainsAny(channelsRef, channelValues);
          return anyExpr ? not(anyExpr) : null;
        }
        default:
          return null;
      }
    }

    case ConversationFilterNames.CONVERSATION_TAGS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      const tagIdsRef = conversationRef.tagIds;
      const tagValues = filter.value ?? [];

      if (tagValues.length === 0) {
        return null;
      }

      switch (filter.operator) {
        case ArrayStringFilterOperator.ANY_OF:
          return arrayFieldContainsAny(tagIdsRef, tagValues);
        case ArrayStringFilterOperator.ALL_OF:
          return arrayFieldContainsAll(tagIdsRef, tagValues);
        case ArrayStringFilterOperator.NONE_OF: {
          const anyExpr = arrayFieldContainsAny(tagIdsRef, tagValues);
          return anyExpr ? not(anyExpr) : null;
        }
        default:
          return null;
      }
    }

    case ConversationFilterNames.LAST_CUSTOMER_MESSAGE_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      return dateFilterToExpression(
        filter,
        conversationRef.lastCustomerMessageAt,
      );
    }

    case ConversationFilterNames.LAST_MERCHANT_MESSAGE_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      return dateFilterToExpression(
        filter,
        conversationRef.lastMerchantMessageAt,
      );
    }

    case ConversationFilterNames.CREATED_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      return dateFilterToExpression(filter, conversationRef.createdAt);
    }

    case ConversationFilterNames.CLOSED_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      // closedAt is represented by statusUpdatedAt when status is CLOSED.
      // We filter on statusUpdatedAt and also require status === "CLOSED".
      const dateExpr = dateFilterToExpression(
        filter,
        conversationRef.statusUpdatedAt,
      );
      if (!dateExpr) {
        return null;
      }
      return and(eq(conversationRef.status, "CLOSED"), dateExpr);
    }

    case ConversationFilterNames.LAST_RESPONSE_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      // lastResponseAt is the more recent of lastCustomerMessageAt and lastMerchantMessageAt.
      // We apply the date filter to both fields and OR them together,
      // since if either timestamp satisfies the filter, the conversation matches.
      const customerExpr = dateFilterToExpression(
        filter,
        conversationRef.lastCustomerMessageAt,
      );
      const merchantExpr = dateFilterToExpression(
        filter,
        conversationRef.lastMerchantMessageAt,
      );
      if (customerExpr && merchantExpr) {
        return or(customerExpr, merchantExpr);
      }
      return customerExpr ?? merchantExpr;
    }

    case ConversationFilterNames.CUSTOMER_TAGS:
    case ConversationFilterNames.MENTIONS:
      return null;

    default:
      return null;
  }
}

/**
 * Converts a FilterGroup to a TanStack DB expression, handling AND/OR logic.
 *
 * Only filters that can be applied client-side will be included.
 * Server-side-only filters are skipped (they're handled via OpenSearch).
 */
export function filterGroupToTanstackExpression(
  group: FilterGroup,
  conversationRef: Ref<ConversationListItemResponse>,
): TanstackExpression | null {
  const expressions: TanstackExpression[] = [];

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      // Recursively process nested groups
      const nestedExpr = filterGroupToTanstackExpression(
        clause,
        conversationRef,
      );
      if (nestedExpr !== null) {
        expressions.push(nestedExpr);
      }
    } else {
      // Process leaf filter
      const expr = advancedFilterToTanstackExpression(clause, conversationRef);
      if (expr !== null) {
        expressions.push(expr);
      }
    }
  }

  return combineExpressions(expressions, group.operator);
}

/**
 * Converts a flat array of AdvancedFilterData to a TanStack DB expression.
 * All filters are combined with AND logic.
 *
 * This is a convenience function for when you don't have a FilterGroup.
 */
export function filtersToTanstackExpression(
  filters: AdvancedFilterData[],
  conversationRef: Ref<ConversationListItemResponse>,
): TanstackExpression | null {
  const expressions: TanstackExpression[] = [];

  for (const filter of filters) {
    const expr = advancedFilterToTanstackExpression(filter, conversationRef);
    if (expr !== null) {
      expressions.push(expr);
    }
  }

  return combineExpressions(expressions, FilterOperator.AND);
}

/**
 * TanStack filter with strongly-typed operators.
 * Discriminated union based on operator type for type-safe value access.
 */
interface TanstackArrayFilter {
  field: (string | number)[];
  operator: ArrayOperator;
  value?: string[];
}

interface TanstackEqualityFilter {
  field: (string | number)[];
  operator: EqualityOperator;
  value?: string;
}

interface TanstackStringPatternFilter {
  field: (string | number)[];
  operator: StringPatternOperator;
  value?: string;
}

interface TanstackNullCheckFilter {
  field: (string | number)[];
  operator: NullCheckOperator;
  value?: undefined;
}

interface TanstackDateComparisonFilter {
  field: (string | number)[];
  operator: DateComparisonOperator;
  value?: string | [string, string];
}

type TanstackFilter =
  | TanstackArrayFilter
  | TanstackEqualityFilter
  | TanstackStringPatternFilter
  | TanstackNullCheckFilter
  | TanstackDateComparisonFilter;

const VALID_OPERATORS = new Set<string>([
  ...STATUS_OPERATORS,
  ...ARRAY_OPERATORS,
  ...STRING_PATTERN_OPERATORS,
  ...NULL_CHECK_OPERATORS,
  ...DATE_COMPARISON_OPERATORS,
]);

function isTanstackFilter(
  input: SimpleComparison,
): input is SimpleComparison & TanstackFilter {
  return VALID_OPERATORS.has(input.operator);
}

/**
 * Maps TanStack field paths to ConversationFilterNames
 */
const FIELD_TO_FILTER_NAME: Record<string, ConversationFilterNames> = {
  status: ConversationFilterNames.STATUS,
  mongoAssigneeId: ConversationFilterNames.ASSIGNEES,
  channels: ConversationFilterNames.CHANNELS,
  tagIds: ConversationFilterNames.CONVERSATION_TAGS,
  customerTags: ConversationFilterNames.CUSTOMER_TAGS,
  mentions: ConversationFilterNames.MENTIONS,
  readAt: ConversationFilterNames.READ_AT,
  summary: ConversationFilterNames.WORDS,
  createdAt: ConversationFilterNames.CREATED_AT,
  statusUpdatedAt: ConversationFilterNames.CLOSED_AT,
  lastCustomerMessageAt: ConversationFilterNames.LAST_CUSTOMER_MESSAGE_AT,
  lastMerchantMessageAt: ConversationFilterNames.LAST_MERCHANT_MESSAGE_AT,
};

/**
 * Maps TanStack operators to ArrayStringFilterOperator
 */
function mapArrayOperator(
  tanstackOp: TanstackFilter["operator"],
): ArrayStringFilterOperator | null {
  switch (tanstackOp) {
    case "eq":
    case "in":
      return ArrayStringFilterOperator.ANY_OF;
    case "not_in":
      return ArrayStringFilterOperator.NONE_OF;
    case "ilike":
    case "like":
      return ArrayStringFilterOperator.CONTAINS;
    default:
      return null;
  }
}

/**
 * Maps TanStack operators to DateFilterOperator
 */
function mapDateOperator(
  tanstackOp: TanstackFilter["operator"],
): DateFilterOperator | null {
  switch (tanstackOp) {
    case "gte":
      return DateFilterOperator.AFTER;
    case "lte":
      return DateFilterOperator.BEFORE;
    case "between":
      return DateFilterOperator.WITHIN;
    default:
      return null;
  }
}

/**
 * Converts a single TanStack filter to AdvancedFilterData.
 * Returns null if the filter cannot be converted.
 */
function tanstackFilterToAdvancedFilter(
  filter: TanstackFilter,
): AdvancedFilterData | null {
  const fieldName = filter.field[0];
  if (typeof fieldName !== "string") {
    return null;
  }

  const filterName = FIELD_TO_FILTER_NAME[fieldName];
  if (!filterName) {
    return null;
  }

  const defaultData = conversationFilterData[filterName];

  switch (defaultData.type) {
    case AdvancedFilterType.ARRAY_TO_STRING: {
      const operator = mapArrayOperator(filter.operator);
      if (!operator) {
        return null;
      }

      let value: string[] = [];
      if (filter.value !== undefined) {
        if (Array.isArray(filter.value)) {
          value = filter.value.filter(
            (v): v is string => typeof v === "string",
          );
        } else if (typeof filter.value === "string") {
          // Handle ilike pattern: strip % wildcards
          const cleanValue = filter.value.replace(/%/g, "");
          value = [cleanValue];
        }
      }

      return {
        name: filterName,
        type: AdvancedFilterType.ARRAY_TO_STRING,
        operator,
        value,
      } as ArrayStringFilterData;
    }

    case AdvancedFilterType.EXISTS: {
      // TanStack uses "isNull" for NOT_EXISTS and "isNotNull" for EXISTS
      let operator: ExistsFilterOperator;
      let value: boolean;

      if (filter.operator === "isNull") {
        operator = ExistsFilterOperator.EXISTS;
        value = false; // isNull means field does NOT exist (unread)
      } else if (filter.operator === "not_isNull") {
        operator = ExistsFilterOperator.EXISTS;
        value = true; // isNotNull means field EXISTS (read)
      } else {
        return null;
      }

      return {
        name: filterName,
        type: AdvancedFilterType.EXISTS,
        operator,
        value,
      } as ExistsFilterData;
    }

    case AdvancedFilterType.DATE: {
      const operator = mapDateOperator(filter.operator);
      if (!operator) {
        return null;
      }

      return {
        name: filterName,
        type: AdvancedFilterType.DATE,
        operator,
        value: filter.value as string | null,
      } as DateFilterData;
    }

    default:
      return null;
  }
}

/**
 * Converts TanStack loadSubsetOptions filters to a FilterGroup.
 * This allows the collection's queryFn to convert TanStack query predicates
 * back to the AdvancedFilterData format for sending to the backend.
 */
export function tanstackFiltersToFilterGroup(
  filters: readonly SimpleComparison[],
): FilterGroup {
  const clauses: AdvancedFilterData[] = [];

  // Track `in` filters per field so multiple single-value `in` filters on the
  // same field (from arrayFieldContainsAll) can be merged into one ALL_OF filter.
  const inFiltersByField = new Map<string, TanstackFilter[]>();

  for (const filter of filters) {
    if (!isTanstackFilter(filter)) {
      continue;
    }

    // Group `in` filters by field path for potential ALL_OF merging
    const fieldKey = filter.field.join(".");
    if (
      filter.operator === "in" &&
      Array.isArray(filter.value) &&
      filter.value.length === 1
    ) {
      const existing = inFiltersByField.get(fieldKey);
      if (existing) {
        existing.push(filter);
        continue;
      }
      inFiltersByField.set(fieldKey, [filter]);
      continue;
    }

    const advancedFilter = tanstackFilterToAdvancedFilter(filter);
    if (advancedFilter) {
      clauses.push(advancedFilter);
    }
  }

  // Process grouped `in` filters: single → ANY_OF, multiple → ALL_OF
  for (const grouped of inFiltersByField.values()) {
    if (grouped.length === 1) {
      for (const item of grouped) {
        const advancedFilter = tanstackFilterToAdvancedFilter(item);
        if (advancedFilter) {
          clauses.push(advancedFilter);
        }
      }
    } else {
      // Merge into a single ALL_OF filter
      const representative = grouped.at(0);
      if (!representative) {
        continue;
      }
      const fieldName = representative.field[0];
      if (typeof fieldName !== "string") {
        continue;
      }
      const filterName = FIELD_TO_FILTER_NAME[fieldName];
      if (!filterName) {
        continue;
      }
      const values = grouped.flatMap((f) =>
        Array.isArray(f.value) ? f.value : [],
      );
      clauses.push({
        name: filterName,
        type: AdvancedFilterType.ARRAY_TO_STRING,
        operator: ArrayStringFilterOperator.ALL_OF,
        value: values,
      } as ArrayStringFilterData);
    }
  }

  return { operator: FilterOperator.AND, clauses };
}
